import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { RoomDto } from '@chromarble/shared-types';
import { Screen, ErrorNote } from '../components/ui';
import { api } from '../services/api';
import { hasCrewReturn, returnToCrew } from '../services/crew-bridge';
import { useSession } from '../services/session';

const STATUS_LABEL: Record<string, string> = {
  WAITING: '대기 중',
  READY: '시작 대기',
  COUNTDOWN: '곧 시작',
  PLAYING: '경기 중',
  FINISHING: '집계 중',
  FINISHED: '경기 종료',
};

/** Accepts a full invite link or "roomId?invite=token" and routes to the join screen. */
function parseInvite(text: string): string | null {
  const t = text.trim();
  try {
    const url = new URL(t, location.origin);
    const m = url.pathname.match(/\/join\/([0-9a-f-]{36})$/i);
    const invite = url.searchParams.get('invite');
    if (m && invite) return `/join/${m[1]}?invite=${encodeURIComponent(invite)}`;
  } catch {
    /* fallthrough */
  }
  return null;
}

export default function HomePage() {
  const navigate = useNavigate();
  const user = useSession((s) => s.user)!;
  const logout = useSession((s) => s.logout);
  const config = useSession((s) => s.config);
  const [rooms, setRooms] = useState<RoomDto[] | null>(null);
  const [showStart, setShowStart] = useState(false);
  const [link, setLink] = useState('');
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!showStart) return;
    api.myRooms().then((r) => setRooms(r.rooms), setError);
  }, [showStart]);

  return (
    <Screen
      title="CHROMARBLE MATCH"
      actions={
        hasCrewReturn() ? (
          <button className="link-btn small" onClick={returnToCrew}>
            Crew로
          </button>
        ) : undefined
      }
    >
      <p className="muted" data-testid="whoami">
        {user.nickname} · {user.tenantId}
      </p>
      <nav className="menu">
        <button className="btn big" onClick={() => setShowStart((v) => !v)} data-testid="menu-start">
          게임 시작
        </button>
        {showStart && (
          <section className="card" data-testid="start-panel">
            <h3>참여 중인 게임방</h3>
            {rooms === null && <p className="muted small">불러오는 중…</p>}
            {rooms?.length === 0 && <p className="muted small">참여 중인 방이 없습니다. 방을 만들거나 Crew에서 받은 초대 링크를 여세요.</p>}
            <ul className="list">
              {rooms?.map((r) => (
                <li key={r.id}>
                  <Link to={r.currentMatchId && ['COUNTDOWN', 'PLAYING'].includes(r.status) ? `/match/${r.currentMatchId}` : `/rooms/${r.id}`}>
                    {r.title} <span className="muted small">· {STATUS_LABEL[r.status] ?? r.status} · {r.members.length}/{r.maxPlayers}명</span>
                  </Link>
                </li>
              ))}
            </ul>
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                const target = parseInvite(link);
                if (target) navigate(target);
                else setError('초대 링크 형식이 올바르지 않습니다.');
              }}
            >
              <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="초대 링크 붙여넣기" aria-label="초대 링크" />
              <button className="btn secondary" type="submit">
                참가
              </button>
            </form>
            <ErrorNote error={error} />
          </section>
        )}
        <Link className="btn big secondary" to="/practice" data-testid="menu-practice">
          개인 연습
        </Link>
        <Link className="btn big secondary" to="/rooms/new" data-testid="menu-create">
          게임방 생성
        </Link>
        <Link className="btn big secondary" to="/leaderboard" data-testid="menu-leaderboard">
          Crew 랭킹
        </Link>
        <Link className="btn big secondary" to="/history" data-testid="menu-history">
          내 전적
        </Link>
      </nav>
      {config?.devAuthEnabled && (
        <p className="center">
          <button className="link-btn small" onClick={logout}>
            개발용 로그아웃
          </button>
        </p>
      )}
    </Screen>
  );
}
